import { userActions } from '@/actions';
import { Button } from '@/components/elements/forms/buttons';
import Input from '@/components/elements/forms/Input/Input';
import Header from '@/components/elements/Header/Header';
import ListItem from '@/components/elements/lists/ListItem/ListItem';
import Snackbar from '@/components/elements/notifications/Snackbar/Snackbar';
import Icon from '@/components/icons/Icon';
import Modal from '@/components/modules/modals/redesign/Modal/Modal';
import { trackMpEvent } from '@/helpers/analyticsTS';
import { useAppSelector } from '@/hooks';
import useMobileView from '@/hooks/useMobileView';
import { _s } from '@/locale';
import { userService } from '@/services';
import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';

const baseTranslationKey = 'account-settings';

const DeletaAccountModal = ({
  isOpen,
  handleClick,
  handleClose,
  confirmEmail,
  submitting,
}: {
  isOpen: boolean;
  handleClick: () => void;
  handleClose: () => void;
  confirmEmail: string;
  submitting: boolean;
}) => {
  const [email, setEmail] = useState('');
  const { isMobileView } = useMobileView();
  const translationKey = `${baseTranslationKey}.delete-account-modal`;

  return (
    <Modal isOpen={isOpen}>
      <Modal.Content floating={!isMobileView}>
        <Modal.Header title={_s(`${translationKey}.title`)} />
        <div className={`gap-sm flex flex-col ${isMobileView ? 'px-lg' : ''}`}>
          <div className="gap-sm flex">
            <div className="flex-shrink-0" style={{ width: 24, height: 24 }}>
              <Icon variant="warning" color="danger-500" />
            </div>
            <p className="text-danger">{_s(`${translationKey}.warning`)}</p>
          </div>
          <Input
            title={_s(`${translationKey}.input-title`, { email: confirmEmail })}
            placeholder=""
            onChange={(e) => setEmail(e.target.value)}
            onBlur={() => null}
            value={email}
          />
        </div>
        <Modal.Footer>
          <Button
            variant="link"
            size="sm"
            loading={submitting}
            className="text-danger"
            onClick={handleClick}
            disabled={email !== confirmEmail}>
            {_s(`${translationKey}.delete-button`)}
          </Button>
          <Button variant="primary" size="sm" onClick={handleClose}>
            {_s(`${translationKey}.cancel-button`)}
          </Button>
        </Modal.Footer>
      </Modal.Content>
    </Modal>
  );
};

const DeleteAccount = () => {
  const [showDeleteAccountModal, setShowDeleteAccountModal] = useState(false);
  const user = useAppSelector((state) => state.users?.user);
  const [deleteUserLoading, setDeleteUserLoading] = useState(false);
  const history = useHistory();
  const appDispatch = useDispatch();

  const handleOnConfirmDeleteAccountClick = () => {
    setDeleteUserLoading(true);
    userService
      .deleteProfile()
      .then(() => {
        toast(({ closeToast }) => (
          <Snackbar
            type="success"
            label={_s(`${baseTranslationKey}.snackbar.delete-account.success`)}
            onClose={closeToast}
          />
        ));

        // @ts-ignore
        appDispatch(userActions.deleteUser());
        setDeleteUserLoading(false);
        setShowDeleteAccountModal(false);
        trackMpEvent({ name: 'delete_account_flow', properties: { step: 'success' } });
        history.push('/'); // Redirect to home
      })
      .catch((error) => {
        toast(({ closeToast }) => (
          <Snackbar
            type="danger"
            label={_s(`${baseTranslationKey}.snackbar.delete-account.error`, { error })}
            onClose={closeToast}
          />
        ));
        setDeleteUserLoading(false);
        setShowDeleteAccountModal(false);

        trackMpEvent({ name: 'delete_account_flow', properties: { step: 'error' } });
      });
  };

  const handleOnDeleteAccountClick = () => {
    trackMpEvent({ name: 'delete_account_flow', properties: { step: 'initiated' } });
    setShowDeleteAccountModal(true);
  };

  const handleOnAbortDeleteAccountClick = () => {
    trackMpEvent({ name: 'delete_account_flow', properties: { step: 'cancel' } });
    setShowDeleteAccountModal(false);
  };

  return (
    <div>
      <DeletaAccountModal
        isOpen={showDeleteAccountModal}
        handleClick={handleOnConfirmDeleteAccountClick}
        handleClose={handleOnAbortDeleteAccountClick}
        confirmEmail={user?.contact?.email || ''}
        submitting={deleteUserLoading}
      />
      <div key="deleteAccount">
        <div className="p-lg">
          <Header label={_s(`${baseTranslationKey}.deleteAccount.section`)} size="lg" />
        </div>
        <ListItem verticalAlign="top" className="pt-0">
          {_s(`${baseTranslationKey}.deleteAccount.label`)}
        </ListItem>
        <div className="px-lg py-lg text-left">
          <Button
            variant="link"
            size="md"
            className="!text-danger-700 pl-0 pt-0"
            leftIcon={<Icon variant="trash" color="danger-700" />}
            onClick={handleOnDeleteAccountClick}>
            {_s(`${baseTranslationKey}.deleteAccount.action`)}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default DeleteAccount;
